<template>
  <div class="w-full">
    <NavigationConfirmModal ref="navigationConfirmModal" />
    <AuditFieldsModal v-if="hasAuditFields(props.entity)" v-model="showAuditFieldsModal" :entity="props.entity" />
    <PageTitle :title="props.title" :subtitle="props.subtitle" :show-back-button="props.showBackButton">
      <template #buttons>
        <slot name="buttons" />
        <VaMenu v-if="effectiveSecondaryActions.length">
          <template #anchor>
            <VaButton color="background-element">...</VaButton>
          </template>

          <VaMenuItem
            v-for="(action, index) in effectiveSecondaryActions.filter((a) => a.visible !== false)"
            :key="index"
            v-permission="[action.requiredPermission, action.requiredAction]"
            :icon="action.icon"
            :disabled="action.disabled"
            @selected="action.action"
          >
            {{ t(action.label) }}
          </VaMenuItem>
        </VaMenu>
      </template>
    </PageTitle>
  </div>
  <VaCard>
    <VaCardContent>
      <slot />
    </VaCardContent>
  </VaCard>
</template>

<script lang="ts" setup>
import { PropType, watch, ref, computed } from "vue"
import { onBeforeRouteLeave } from "vue-router"
import { PermissionAction } from "./core/helpers/securityTypes"
import { useI18n } from "vue-i18n"
import NavigationConfirmModal from "@shared/components/NavigationConfirmModal.vue"
import AuditFieldsModal from "@shared/components/AuditFieldsModal.vue"
import { hasAuditFields } from "@shared/helpers/utils"

const { t } = useI18n()
const navigationConfirmModal = ref()
const showAuditFieldsModal = ref(false)

const title = ref<string>("")
const subtitle = ref<string>("")
const hasUnsavedChanges = ref<boolean>(false)

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: "" },
  showBackButton: { type: Boolean, default: false },
  hasUnsavedChanges: { type: Boolean, default: false },
  entity: { type: Object as PropType<Record<string, any>>, default: null },
  secondaryActions: {
    type: Array as PropType<
      Array<{
        label: string
        icon: string
        visible?: boolean
        disabled?: boolean
        requiredPermission?: string
        requiredAction?: PermissionAction
        action: () => void
      }>
    >,
    default: () => [],
    validator: (actions: any[]) => actions.every((a) => typeof a.label === "string" && typeof a.action === "function"),
  },
})

const effectiveSecondaryActions = computed(() => {
  if (!hasAuditFields(props.entity)) return props.secondaryActions
  return [
    ...props.secondaryActions,
    {
      label: "general.auditFields",
      icon: "manage_search",
      action: () => { showAuditFieldsModal.value = true },
    },
  ]
})

watch(
  () => props.title,
  (newValue) => {
    title.value = newValue
  },
)

watch(
  () => props.subtitle,
  (newValue) => {
    subtitle.value = newValue
  },
)

watch(
  () => props.hasUnsavedChanges,
  (newValue) => {
    hasUnsavedChanges.value = newValue ?? false
  },
)

onBeforeRouteLeave(async (to: any, from: any, next: any) => {
  if (hasUnsavedChanges.value) {
    const sureToLeave = await navigationConfirmModal.value?.show()
    if (sureToLeave) next()
    else next(false)
  } else next()
})
</script>
